import React, { createContext, Fragment, useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Mail, ArrowLeft, CheckCircle } from "lucide-react";

import { Button } from "@/components/ui/button.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Alert, AlertDescription } from "@/components/ui/alert.jsx";
import { useToast } from "@/hooks/use-toast.js";
import authAPI, { applyServerErrors, mapAuthErrors } from "@/lib/api/auth";

// Password reset request validation schema
const resetRequestSchema = z.object({
  email: z.string().email("Adresse e-mail invalide"),
});

const PasswordResetRequestContext = createContext(null);

function PasswordResetRequestProvider({ children }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const navigate = useNavigate();
  const { toast } = useToast();

  const resetRequestForm = useForm({
    resolver: zodResolver(resetRequestSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async ({ email }) => {
    setIsLoading(true);

    const response = await authAPI.requestPasswordResetEMail(email);

    if (!response.ok) {
      const jsonErrors = await response.json();
      const mapped = mapAuthErrors(jsonErrors.errors);
      applyServerErrors(resetRequestForm.setError, mapped);
      setIsLoading(false);
      return;
    }

    setIsSuccess(true);
    toast({
      title: "Email envoyé",
      description:
        "Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.",
    });
    setIsLoading(false);
  };

  const handleBackToLogin = () => {
    navigate("/login", { replace: true });
  };

  const value = {
    isLoading,
    setIsLoading,
    isSuccess,
    setIsSuccess,
    resetRequestForm,
    onSubmit,
    handleBackToLogin,
  };

  return (
    <PasswordResetRequestContext.Provider value={value}>
      {children}
    </PasswordResetRequestContext.Provider>
  );
}

function usePasswordResetRequest() {
  return useContext(PasswordResetRequestContext);
}

export default function PasswordResetRequest() {
  return (
    <PasswordResetRequestProvider>
      <PasswordResetRequestComponent />
    </PasswordResetRequestProvider>
  );
}

function PasswordResetRequestComponent() {
  const { isSuccess } = usePasswordResetRequest();

  if (isSuccess) return <PasswordResetRequestSuccess />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <PasswordResetRequestHeader />
        </CardHeader>

        <CardContent className="space-y-6">
          <PasswordResetRequestForm />
        </CardContent>
      </Card>
    </div>
  );
}

function PasswordResetRequestForm() {
  const { isLoading, resetRequestForm, onSubmit, handleBackToLogin } =
    usePasswordResetRequest();

  return (
    <Fragment>
      <form
        onSubmit={resetRequestForm.handleSubmit(onSubmit)}
        className="space-y-6"
      >
        <div className="space-y-2">
          <Label htmlFor="email">Adresse e-mail</Label>
          <Input
            id="email"
            type="email"
            placeholder="votre@email.com"
            {...resetRequestForm.register("email")}
            className={
              resetRequestForm.formState.errors.email
                ? "border-destructive"
                : ""
            }
            disabled={isLoading}
            autoFocus
          />
          {resetRequestForm.formState.errors.email && (
            <p className="text-sm text-destructive">
              {resetRequestForm.formState.errors.email.message}
            </p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Envoi en cours...
            </>
          ) : (
            "Envoyer le mail de réinitialisation"
          )}
        </Button>
      </form>

      <div className="text-center">
        <Button
          variant="ghost"
          className="text-sm text-muted-foreground hover:text-primary"
          onClick={handleBackToLogin}
          disabled={isLoading}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour à la connexion
        </Button>
      </div>
    </Fragment>
  );
}

function PasswordResetRequestHeader() {
  return (
    <Fragment>
      <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
        <Mail className="h-6 w-6 text-primary" />
      </div>
      <CardTitle className="text-2xl font-bold">
        Réinitialiser le mot de passe
      </CardTitle>
      <CardDescription>
        Entrez votre adresse email pour recevoir un mail de réinitialisation
      </CardDescription>
    </Fragment>
  );
}

function PasswordResetRequestSuccess() {
  const { handleBackToLogin } = usePasswordResetRequest();

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-green-800">
            Email envoyé
          </CardTitle>
          <CardDescription className="text-green-700">
            Vérifiez votre email pour le lien de réinitialisation
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              Si un compte existe avec l'adresse email que vous avez fournie,
              vous recevrez un email contenant un lien pour réinitialiser votre
              mot de passe.
            </p>

            <div className="space-y-3">
              <Button
                onClick={handleBackToLogin}
                variant="ghost"
                className="w-full"
              >
                Retour à la connexion
              </Button>
            </div>
          </div>

          <div className="text-xs text-center text-muted-foreground space-y-1">
            <p>L'email peut prendre quelques minutes à arriver</p>
            <p>Vérifiez votre dossier spam si vous ne le recevez pas</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
